import { TechshowPortalContent } from "@app/types/ServerCustomContext";
import { Button, Dropdown, Typography } from "@sphtech/dmg-design-system";
import cx from "classnames";
import { useState } from "react";

import styles from "./TechshowPortal.module.css";
import {
  BASE_BROCHURE_PATH,
  BASE_BROCHURE_VIEW_PATH,
  buildFilterSegment,
  getBrandFilterState,
  getCategoryFilterState,
  joinPaths,
} from "./utils";

type Props = {
  brochureSection: {
    title: string;
    brochureIds: string[];
  };
  brochureImages: TechshowPortalContent["brochureImages"];
  filterBrands: TechshowPortalContent["filterBrands"];
  filterCategories: TechshowPortalContent["filterCategories"];
  filterMap: TechshowPortalContent["filterMap"];
  max?: number;
  selectedBrand?: string;
  selectedCategory?: string;
  hideHeading?: boolean;
};

export default function TechshowPortalBrochures({
  brochureSection,
  brochureImages,
  filterBrands,
  filterCategories,
  filterMap,
  max = 0,
  selectedBrand = "",
  selectedCategory = "",
  hideHeading = false,
}: Props) {
  const { DateLine, DateTime, Heading } = Typography;

  const displayedBrochureImages =
    max > 0 ? brochureImages.slice(0, max) : brochureImages;

  const [updatedBrand, setUpdatedBrand] = useState(selectedBrand);
  const [updatedCategory, setUpdatedCategory] = useState(selectedCategory);

  const { brandOptions, brandValue } = getBrandFilterState(
    filterBrands,
    filterMap,
    updatedBrand,
    updatedCategory,
  );

  const { categoryOptions, categoryValue } = getCategoryFilterState(
    filterCategories,
    filterMap,
    updatedCategory,
    updatedBrand,
  );

  const FILTER_PATH = [
    BASE_BROCHURE_PATH,
    buildFilterSegment(updatedCategory, updatedBrand),
  ]
    .filter(Boolean)
    .join("/");

  const brochureViewUrlFilter = buildFilterSegment(
    selectedCategory,
    selectedBrand,
  );

  return (
    <section
      className={cx(styles.bodyContainer, styles.tecshowBrochuresImages)}
    >
      {!hideHeading && (
        <Heading.SubHeading>{brochureSection.title}</Heading.SubHeading>
      )}

      <div className={styles.brochureFilter}>
        <div className={styles.brochureFilterLabel}>Filter By:</div>
        <Dropdown
          label=""
          name="brands"
          id="brands_select"
          value={brandValue}
          options={brandOptions}
          onChange={(event) => {
            setUpdatedBrand(event.target.value);
          }}
        />
        <Dropdown
          label=""
          name="categories"
          id="categories_select"
          value={categoryValue}
          options={categoryOptions}
          onChange={(event) => {
            setUpdatedCategory(event.target.value);
          }}
        />
        <Button variant="primary" href={FILTER_PATH}>
          Go
        </Button>
      </div>
      <div className={styles.techshowBrochureLinks}>
        {displayedBrochureImages.map((brochureImage, key) => {
          if (brochureSection.brochureIds.includes(brochureImage.brochureId)) {
            const brochureViewUrl = joinPaths(
              BASE_BROCHURE_VIEW_PATH,
              `${brochureImage.id}`,
              brochureViewUrlFilter,
            );
            const brochureImageOPortraitUrl =
              brochureImage.fields?.["portrait-caas"]?.url || "";
            const brochureThumbnailUrl = `${brochureImageOPortraitUrl}?w=360`;
            return (
              <a
                key={key}
                href={brochureViewUrl}
                target="_blank"
                className={styles.techshowBrochureLink}
                rel="noreferrer"
              >
                <img src={brochureThumbnailUrl} alt="" />
                <Heading.SubHeading2>
                  {brochureImage.brochureTitle}
                </Heading.SubHeading2>

                {brochureImage.brochureDate && (
                  <DateLine>
                    <DateTime date={new Date(brochureImage.brochureDate)} />
                  </DateLine>
                )}

                <div className={styles.techshowBrochureLinkCta}>View Image</div>
              </a>
            );
          } else {
            return "";
          }
        })}
      </div>
      {max > 0 && (
        <div className={styles.brochureViewAll}>
          <Button variant="secondary" href={BASE_BROCHURE_PATH}>
            View All
          </Button>
        </div>
      )}
    </section>
  );
}
